import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Avatar, Chip, CircularProgress } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { format } from 'date-fns';
import groupBy from 'lodash/groupBy';
import clsx from 'clsx';
import fileDownload from 'js-file-download';

import { formatPrice, getPayoutLabelName } from 'utils/helpers';
import { openModal } from 'features/utilsSlice';
import axios from 'utils/axios';
import LeftArrowIcon from 'public/icons/left-arrow.svg';
import RightArrowIcon from 'public/icons/right-arrow.svg';
import DollarIcon from 'public/icons/dollar.svg';
import GigSocialIcon from 'public/images/favicon.svg';
import NoAvatarImage from 'public/images/no-avatar.svg';
import TransactionInfoDialog from './TransactionInfoDialog';
import DocumentIcon from 'public/icons/document.svg';
import CloseIcon from 'public/icons/close-main.svg';
import styles from 'styles/components/Wallet.module.scss';

const size = { width: 50, height: 50 };
const green = {
  filter: 'invert(20%) sepia(7%) saturate(2701%) hue-rotate(92deg) brightness(86%) contrast(86%)',
};
const red = {
  filter:
    'invert(50%) sepia(43%) saturate(2155%) hue-rotate(330deg) brightness(108%) contrast(113%)',
};
const grey = {
  filter: 'invert(50%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(90%) contrast(90%)',
};
const alertStyle = { width: 'auto', margin: '0 15px' };
const avatarFullSize = { width: '100%', height: '100%' };

const Transactions = ({
  walletId,
  transactions,
  isLoadingWallet,
  title = 'Transactions',
  noMessage = 'No transactions',
}) => {
  const dispatch = useDispatch();

  const sortTransactions = transactions =>
    transactions?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const groupedTransactions = groupBy(transactions, transaction =>
    format(new Date(transaction?.created_at), 'MMM Y')
  );

  const getAvatarIcon = transaction => {
    if (title === 'Payouts' || transaction.kind === 'payout') {
      return (
        <div className={styles.iconContainer}>
          <DollarIcon width={20} height={26} />
        </div>
      );
    } else if (transaction.kind === 'refunded_in_rytepay') {
      return (
        <div className={styles.iconContainer} style={{ opacity: 0.5 }}>
          <CloseIcon width={20} height={20} style={grey} />
        </div>
      );
    } else if (transaction.kind === 'refund') {
      return <GigSocialIcon width={50} height={50} />;
    } else if (transaction.kind === 'credit') {
      return (
        <Avatar src={transaction.sender.avatar_url} alt="Profile" style={size}>
          <NoAvatarImage style={avatarFullSize} />
        </Avatar>
      );
    }

    return (
      <Avatar src={transaction.recipient.avatar_url} alt="Profile" style={size}>
        <NoAvatarImage style={avatarFullSize} />
      </Avatar>
    );
  };

  const getTitle = transaction => {
    if (title === 'Payouts') return transaction.description;

    if (transaction.kind === 'refunded_in_rytepay') {
      return 'Voided - Refunded Externally';
    } else if (transaction.kind === 'refund') {
      return transaction.title || 'Refund';
    } else if (transaction.kind === 'payout') {
      return `${transaction.description}`;
    } else if (transaction.kind === 'debit') {
      return `To ${transaction.recipient?.display_name}`;
    } else if (
      transaction.description.includes('affiliate fee') ||
      transaction.description.includes('referral fee')
    ) {
      return `${transaction.sender?.display_name} Referral Fee`;
    }

    return `From ${transaction.sender?.display_name}`;
  };

  const handleDownload = () =>
    axios
      .get(`v1/wallets/${walletId}/export_transactions_csv`, {
        responseType: 'blob',
      })
      .then(response => {
        fileDownload(response.data, `gig-social-transactions-${new Date()}.csv`);
      })
      .catch(() => {});

  return (
    <>
      {transactions?.length === 0 && !isLoadingWallet && (
        <Alert style={alertStyle} severity="info">
          {noMessage}
        </Alert>
      )}

      {transactions?.length === 0 && isLoadingWallet && <CircularProgress color="primary" />}

      {transactions?.length > 0 && !isLoadingWallet && (
        <div className={styles.content}>
          {title && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                paddingRight: 15,
              }}
            >
              <h2>{title}</h2>

              {title === 'Transactions' && (
                <DocumentIcon
                  width={30}
                  height={30}
                  className="hover-icon"
                  onClick={handleDownload}
                />
              )}
            </div>
          )}

          {Object.entries(groupedTransactions).map(([transactionDate, transactions]) => (
            <div key={transactionDate}>
              <h4 style={title ? {} : { marginTop: 0 }}>{transactionDate.toUpperCase()}</h4>

              {sortTransactions(transactions).map(transaction => (
                <div className={styles.transaction} key={transaction.id}>
                  <div style={{ display: 'flex' }}>
                    {getAvatarIcon(transaction)}

                    <div className={styles.details}>
                      <div>
                        <span className={styles.title}>{getTitle(transaction)}</span>
                        {transaction.kind === 'credit' &&
                          transaction.recipient &&
                          transaction.recipient.display_name && (
                            <span className={styles.title}>
                              To {transaction.recipient.display_name}
                            </span>
                          )}

                        <span className={styles.date}>
                          {format(new Date(transaction.created_at), 'MMM d y, hh:mm a')}
                        </span>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <div
                          className={styles.price}
                          style={
                            transaction.kind === 'refunded_in_rytepay'
                              ? { opacity: 0.5, textDecoration: 'line-through', color: '#888' }
                              : transaction.cleared
                              ? { opacity: 1 }
                              : { opacity: 0.5 }
                          }
                        >
                          {transaction.kind === 'refunded_in_rytepay'
                            ? ''
                            : transaction.kind === 'credit' || transaction.kind === 'refund'
                            ? '+'
                            : '-'}{' '}
                          {formatPrice(transaction.amount)}
                          {transaction.kind === 'refunded_in_rytepay' ? (
                            <RightArrowIcon style={grey} />
                          ) : transaction.kind === 'credit' || transaction.kind === 'refund' ? (
                            <LeftArrowIcon style={green} />
                          ) : (
                            <RightArrowIcon style={red} />
                          )}
                        </div>

                        {!transaction.cleared &&
                          ['credit', 'refund'].includes(transaction.kind) && (
                            <Chip
                              label="Pending cc clearing"
                              className={clsx('light-purple-chip', styles.chip)}
                              onClick={() => dispatch(openModal({ name: 'Transaction Info' }))}
                            />
                          )}
                      </div>

                      {title === 'Payouts' && (
                        <Chip
                          label={getPayoutLabelName(transaction.status)}
                          className={clsx('light-purple-chip', styles.payoutChip, {
                            [styles.redChip]:
                              getPayoutLabelName(transaction.status) === 'Cancelled',
                            [styles.purpleChip]: transaction.status === 'completed',
                          })}
                        />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      <TransactionInfoDialog />
    </>
  );
};

Transactions.propTypes = {
  transactions: PropTypes.array,
  isLoadingWallet: PropTypes.bool,
};

Transactions.defaultProps = {
  isLoadingWallet: false,
  transactions: [],
};

export default Transactions;
